import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';

/**
 * Firebase設定
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

/**
 * Firebase Analytics 上で識別する環境名。
 * GA4 のフィルタ・比較で使う `app_env` ユーザープロパティの値域。
 */
export type AppEnv = 'local' | 'staging' | 'production';

/**
 * Vite の `MODE` から `app_env` をマップする。
 * `npm run dev` → 'local' / `vite build --mode staging` → 'staging' /
 * `vite build --mode prod` または `vite build`（既定の MODE=production）→ 'production'。
 */
export const getAppEnv = (): AppEnv => {
  const mode = import.meta.env.MODE;
  if (mode === 'staging') return 'staging';
  if (mode === 'prod' || mode === 'production') return 'production';
  return 'local';
};

let app: FirebaseApp;
let auth: Auth;
let analyticsPromise: Promise<Analytics | null> | null = null;

/**
 * Firebaseアプリを初期化
 *
 * Analytics は本番系（staging / production）でのみ有効化する。
 * `isSupported()` の解決を待つため `Promise<Analytics | null>` で保持し、
 * 取得は `getFirebaseAnalytics()` 経由とする。
 *
 * @returns なし
 */
export const initializeFirebase = (): void => {
  if (!app) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    auth.languageCode = 'ja';
    if (getAppEnv() === 'local') {
      analyticsPromise = Promise.resolve(null);
    } else {
      analyticsPromise = isSupported().then((supported) => (supported ? getAnalytics(app) : null));
    }
  }
};

/**
 * Firebase Authインスタンスを取得
 * @returns Firebase Authインスタンス
 */
export const getFirebaseAuth = (): Auth => {
  if (!auth) {
    initializeFirebase();
  }
  return auth;
};

/**
 * Firebase Analytics インスタンスを取得する。
 *
 * - local 環境では常に `null` を返す（Analytics を発火させない）
 * - ブラウザ環境が Analytics 非対応な場合も `null`
 * - 上記以外では Analytics インスタンスを返す
 *
 * @returns Analytics インスタンス、もしくは無効化されている場合は null
 */
export const getFirebaseAnalytics = (): Promise<Analytics | null> => {
  if (!analyticsPromise) {
    initializeFirebase();
  }
  // initializeFirebase 後は必ずセットされている
  return analyticsPromise!;
};
